import React, { useState, useRef, useCallback } from 'react';
import { X, Download, Share2, Link2, Check, BookOpen, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { toBlob } from 'html-to-image';
import axios from 'axios';
import { API_URL as API } from '@/utils/apiConfig';
import { shareViaNative } from '@/utils/nativeShare';
import ShareToFriendModal from '@/components/ShareToFriendModal';
import { SHARE_THEMES } from '@/components/shareThemes';
import { useFeatureFlag } from '@/contexts/FeatureFlagsContext';

import { useTranslation } from 'react-i18next';

import { isDespiaIOS, saveToIOSPhotos } from '@/utils/shareCard';
/*
 * Reflection share — uses the unified SHARE_THEMES palette (parity with
 * ShareModal / ShareDYKModal). Card layout is reflection-specific
 * (topic + intro + body + thought + scripture chips) but the colour
 * tokens are shared so the in-app preview, the html2canvas image, and
 * the server-rendered PNG fallback all look like the same family.
 */
const THEMES = SHARE_THEMES;

function stripMarkdownClean(text) {
  if (!text) return '';
  return text
    .replace(/\*\*\*(.*?)\*\*\*/g, '$1')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function truncateBody(text, maxLen = 400) {
  const clean = stripMarkdownClean(text);
  if (clean.length <= maxLen) return clean;
  const truncated = clean.substring(0, maxLen);
  const lastPeriod = truncated.lastIndexOf('.');
  const cutAt = lastPeriod > maxLen - 80 ? lastPeriod : maxLen;
  return truncated.substring(0, cutAt + 1) + '\n...';
}

/* ── The actual card rendered to image ── */
function ReflectionShareCard({ theme, reflection }) {
  const {
    t
  } = useTranslation();

  const cleanBody = truncateBody(reflection.body);
  const cleanIntro = stripMarkdownClean(reflection.introduction);
  const dateStr = new Date(reflection.date + 'T12:00:00').toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });

  return (
    <div
      style={{
        background: theme.bg,
        padding: '36px 32px 28px',
        fontFamily: "'Georgia', 'Times New Roman', serif",
        minHeight: 280,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Decorative side accent */}
      <div
        style={{
          position: 'absolute',
          top: 0, left: 0, bottom: 0,
          width: 5,
          background: theme.accent,
        }}
      />
      {/* Date */}
      <p
        style={{
          fontSize: 11,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: theme.secondary,
          marginBottom: 10,
          fontFamily: "'Helvetica Neue', Arial, sans-serif",
        }}
      >
        {dateStr}
      </p>
      {/* Topic */}
      <h2
        style={{
          fontSize: 22,
          fontWeight: 700,
          color: theme.topicColor,
          marginBottom: 12,
          lineHeight: 1.3,
        }}
      >
        {reflection.topic}
      </h2>
      {/* Intro */}
      <p
        style={{
          fontSize: 13,
          lineHeight: 1.65,
          color: theme.secondary,
          marginBottom: 16,
          fontStyle: 'italic',
        }}
      >
        {cleanIntro}
      </p>
      {/* Divider */}
      <div style={{ height: 1, background: theme.border, marginBottom: 16 }} />
      {/* Body excerpt */}
      <div
        style={{
          fontSize: 13,
          lineHeight: 1.75,
          color: theme.text,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          marginBottom: 18,
        }}
      >
        {cleanBody}
      </div>
      {/* Thought to consider */}
      <div
        style={{
          background: theme.tagBg,
          border: `1px solid ${theme.border}`,
          borderRadius: 8,
          padding: '14px 16px',
          marginBottom: 18,
        }}
      >
        <p
          style={{
            fontSize: 11,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: theme.accent,
            marginBottom: 6,
            fontFamily: "'Helvetica Neue', Arial, sans-serif",
            fontWeight: 600,
          }}
        >
          {t('generated.sharereflectionmodal_something_to_consider_3405')}
        </p>
        <p
          style={{
            fontSize: 13,
            lineHeight: 1.6,
            fontStyle: 'italic',
            color: theme.text,
          }}
        >
          {stripMarkdownClean(reflection.thought_to_consider)}
        </p>
      </div>
      {/* Scripture tags */}
      {reflection.scripture_references?.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 18, justifyContent: 'center' }}>
          {reflection.scripture_references.slice(0, 5).map((ref, i) => (
            <span
              key={i}
              style={{
                fontSize: 10,
                padding: '4px 10px',
                borderRadius: 12,
                background: theme.tagBg,
                color: theme.accent,
                border: `1px solid ${theme.border}`,
                fontFamily: "'Helvetica Neue', Arial, sans-serif",
              }}
            >
              {ref}
            </span>
          ))}
        </div>
      )}
      {/* Footer */}
      <div
        style={{
          paddingTop: 14,
          borderTop: `1px solid ${theme.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <img
            src="/logo.png"
            alt={t('generated.sharereflectionmodal_eili_28ff')}
            style={{ width: 22, height: 22, objectFit: 'contain' }}
            crossOrigin="anonymous"
          />
          <span
            style={{
              fontFamily: "'Playwrite ES Deco', cursive",
              fontSize: 14,
              fontWeight: 700,
              color: theme.accent,
            }}
          >
            {t('generated.sharereflectionmodal_eili_28ff')}
          </span>
        </div>
        <span
          style={{
            fontSize: 10,
            color: theme.secondary,
            fontFamily: "'Helvetica Neue', Arial, sans-serif",
          }}
        >
          {t('generated.sharereflectionmodal_daily_reflection_askeili_82a7')}
        </span>
      </div>
    </div>
  );
}

export default function ShareReflectionModal({ reflection, onClose }) {
  const {
    t
  } = useTranslation();

  const [selectedTheme, setSelectedTheme] = useState('dawn');
  const [generating, setGenerating] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [showShareToFriend, setShowShareToFriend] = useState(false);
  // Hide the "Friend" (share-to-community) button when community_share is OFF.
  const { enabled: communityShareEnabled } = useFeatureFlag('community_share');
  const cardRef = useRef(null);
  const generatingRef = useRef(false);

  // Pre-baked PNG File for sharing. Computed proactively in the
  // background as soon as the preview card is rendered so that on
  // click the user-activation token is still fresh and `navigator.share`
  // fires synchronously — critical for Android, where html2canvas
  // taking 200-500ms on the click would expire the activation and
  // cause the share sheet to silently not appear.
  const [preparedFile, setPreparedFile] = useState(null);
  const preparedKeyRef = useRef(null);  // tracks which theme the file was made for

  const theme = THEMES.find((t) => t.key === selectedTheme) || THEMES[0];

  const ensureShareUrl = useCallback(async () => {
    if (shareUrl) return shareUrl;
    try {
      const res = await axios.post(
        `${API}/reflections/${reflection.reflection_id}/share`,
        {},
        { withCredentials: true }
      );
      const url = `${window.location.origin}/api/og/share/${res.data.share_id}`;
      setShareUrl(url);
      return url;
    } catch {
      return null;
    }
  }, [shareUrl, reflection.reflection_id]);

  const generateImage = useCallback(async () => {
    if (!cardRef.current) return null;
    const blob = await toBlob(cardRef.current, {
      pixelRatio: 2,
      skipAutoScale: true,
      cacheBust: false,
    });
    return blob;
  }, []);

  // Eagerly resolve the share URL on mount.
  React.useEffect(() => {
    ensureShareUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pre-bake the PNG in the background. Does NOT clear the old file
  // during theme switches — button stays enabled while new image bakes.
  React.useEffect(() => {
    if (!cardRef.current) return;
    const key = selectedTheme;
    if (preparedKeyRef.current === key) return;
    let cancelled = false;
    (async () => {
      try {
        const blob = await generateImage();
        if (cancelled || !blob) return;
        const file = new File([blob], `eili-reflection-${reflection.date}.png`, { type: 'image/png' });
        preparedKeyRef.current = key;
        setPreparedFile(file);
      } catch (err) {
        console.warn('[ShareReflectionModal] pre-bake failed:', err);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTheme, reflection.date]);

  // Upload the pre-baked PNG as OG image — background, non-blocking.
  const ogUploadedRef = useRef(null);
  React.useEffect(() => {
    if (!preparedFile || !shareUrl) return;
    const match = shareUrl.match(/\/share\/([^/]+)$/);
    if (!match) return;
    const shareId = match[1];
    const key = `${shareId}|${preparedFile.size}`;
    if (ogUploadedRef.current === key) return;
    ogUploadedRef.current = key;
    const form = new FormData();
    form.append('file', preparedFile, 'eili-share.png');
    fetch(`${API}/share/${shareId}/og-image`, { method: 'POST', body: form, credentials: 'include' }).catch(() => {});
  }, [preparedFile, shareUrl]);


  const handleDownload = async () => {
    if (generatingRef.current) return;
    generatingRef.current = true;
    setGenerating(true);
    try {
      const blob = await generateImage();
      if (!blob) return;

      const isDespiaAndroid = document.body?.classList?.contains('despia-android');
      if (isDespiaAndroid) {
        toast.info(t('generated.sharereflectionmodal_saving_to_device_1f7d'));
        const form = new FormData();
        form.append('file', new File([blob], `eili-reflection-${reflection.date}.png`, { type: 'image/png' }), 'eili-reflection.png');
        const res = await fetch(`${API}/share/upload-image`, { method: 'POST', body: form, credentials: 'include' });
        const data = await res.json();
        if (data?.url) {
          const { default: despia } = await import('despia-native');
          despia(data.url);
        }
      } else if (isDespiaIOS()) {
        await saveToIOSPhotos(blob, `eili-reflection-${reflection.date}.png`);
      } else {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.download = `eili-reflection-${reflection.date}.png`;
        link.href = url;
        link.click();
        URL.revokeObjectURL(url);
        toast.success(t('generated.sharereflectionmodal_image_saved_4705'));
      }
    } finally {
      generatingRef.current = false;
      setGenerating(false);
    }
  };

  const handleShareImage = async () => {
    if (generatingRef.current) return;
    const file = preparedFile;
    if (!file) {
      toast.error(t('generated.sharereflectionmodal_share_failed_please_081a'));
      return;
    }

    let url = shareUrl;
    if (!url) url = await ensureShareUrl();

    const result = await shareViaNative({
      title: `Eili — ${reflection.topic}`,
      text: t('generated.sharereflectionmodal_daily_reflection_0488'),
      url: url || undefined,
      file,
    });

    if (!result.shared && !result.aborted) {
      console.warn('[ShareReflectionModal] share failed:', result);
      toast.error(t('generated.sharereflectionmodal_share_failed_please_081a'));
    }
  };

  const handleCopyLink = async () => {
    const url = await ensureShareUrl();
    if (!url) {
      toast.error(t('generated.sharereflectionmodal_could_not_generate_e0bf'));
      return;
    }
    await navigator.clipboard.writeText(url);
    setLinkCopied(true);
    toast.success(t('generated.sharereflectionmodal_link_copied_0080'));
    setTimeout(() => setLinkCopied(false), 2000);
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="share-reflection-modal"
    >
      <div
        className="relative w-full max-w-lg flex flex-col rounded-2xl overflow-hidden"
        style={{
          background: 'var(--theme-surface, #1e293b)',
          border: '1px solid var(--glass-border, #334155)',
          // Keyboard-aware max height — `--visual-vh` shrinks when the iOS
          // soft keyboard opens, so the modal stays fully on-screen.
          maxHeight: 'min(90vh, calc(var(--visual-vh, 100vh) - 32px))',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'var(--glass-border, #334155)' }}>
          <div className="flex items-center gap-2">
            <BookOpen className="h-5 w-5" style={{ color: 'var(--theme-accent)' }} />
            <h3 className="text-base font-semibold" style={{ color: 'var(--theme-text)' }}>
              {t('generated.sharereflectionmodal_share_reflection_d82e')}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full transition hover:bg-white/10"
            data-testid="share-reflection-close"
          >
            <X className="h-4 w-4" style={{ color: 'var(--theme-textSecondary)' }} />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {/* Theme Picker */}
          <div>
            <p className="text-xs font-medium uppercase tracking-wider mb-3" style={{ color: 'var(--theme-textSecondary)' }}>
              {t('generated.sharereflectionmodal_style_4a94')}
            </p>
            <div className="flex gap-2.5 flex-wrap" data-testid="share-reflection-theme-picker">
              {THEMES.map((t) => {
                const active = selectedTheme === t.key;
                return (
                  <button
                    key={t.key}
                    onClick={() => { setSelectedTheme(t.key); }}
                    className="flex flex-col items-center gap-1.5 transition-all"
                    data-testid={`share-reflection-theme-${t.key}`}
                  >
                    <div
                      className="w-10 h-10 rounded-full relative overflow-hidden transition-all"
                      style={{
                        background: t.swatch[0],
                        border: active ? `2.5px solid ${t.swatch[1]}` : '2px solid transparent',
                        boxShadow: active ? `0 0 0 2px ${t.swatch[1]}40` : 'none',
                      }}
                    >
                      <div
                        className="absolute bottom-0 right-0 w-5 h-5 rounded-tl-full"
                        style={{ background: t.swatch[1] }}
                      />
                    </div>
                    <span
                      className="text-[10px] font-medium"
                      style={{ color: active ? 'var(--theme-text)' : 'var(--theme-textSecondary)' }}
                    >
                      {t.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Image Preview */}
          <div>
            <p className="text-xs font-medium uppercase tracking-wider mb-3" style={{ color: 'var(--theme-textSecondary)' }}>
              {t('generated.sharereflectionmodal_preview_0031')}
            </p>
            <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--glass-border)' }}>
              <div ref={cardRef} data-testid="share-reflection-preview">
                <ReflectionShareCard theme={theme} reflection={reflection} />
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="px-5 py-4 border-t flex flex-wrap gap-2.5" style={{ borderColor: 'var(--glass-border, #334155)' }}>
          <Button
            onClick={handleShareImage}
            disabled={generating || !preparedFile}
            className="flex-1 min-w-[120px] rounded-xl py-4 font-medium gap-2"
            style={{ background: theme.accent, color: '#fff' }}
            data-testid="share-reflection-image-btn"
          >
            {!preparedFile && !generating ? (
              <>
                <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                {t('generated.sharereflectionmodal_preparing_09b8')}
              </>
            ) : (
              <>
                <Share2 className="h-4 w-4" />
                {generating ? 'Generating...' : (document.body?.classList?.contains('despia-android') ? 'Share' : 'Share Image')}
              </>
            )}
          </Button>
          <Button
            onClick={handleDownload}
            disabled={generating}
            variant="outline"
            className="rounded-xl py-4 font-medium gap-2"
            style={{ borderColor: 'var(--glass-border)', color: 'var(--theme-text)' }}
            data-testid="share-reflection-download-btn"
          >
            <Download className="h-4 w-4" />
            {t('generated.sharereflectionmodal_save_a9df')}
          </Button>
          <Button
            onClick={handleCopyLink}
            variant="outline"
            className="rounded-xl py-4 font-medium gap-2"
            style={{ borderColor: 'var(--glass-border)', color: 'var(--theme-text)' }}
            data-testid="share-reflection-copy-link-btn"
          >
            {linkCopied ? <Check className="h-4 w-4" /> : <Link2 className="h-4 w-4" />}
            {linkCopied ? 'Copied' : 'Link'}
          </Button>
          {communityShareEnabled && (
          <Button
            onClick={() => setShowShareToFriend(true)}
            variant="outline"
            className="rounded-xl py-4 font-medium gap-2"
            style={{ borderColor: 'var(--glass-border)', color: 'var(--theme-text)' }}
            data-testid="share-reflection-to-friend-btn"
          >
            <Users className="h-4 w-4" />
            {t('generated.sharereflectionmodal_friend_b9a4')}
          </Button>
          )}
        </div>
      </div>
      {/* Send to friend in-community — opens the chat-style share flow.
          The recipient will see this in their per-friend thread and can
          tap to open the actual reflection. */}
      {showShareToFriend && (
        <ShareToFriendModal
          reflection={reflection}
          onClose={() => setShowShareToFriend(false)}
        />
      )}
    </div>
  );
}
